/* eslint-disable no-useless-return */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ProfileDataType, ProfileField } from '../../types/profile';
import { CustomFieldResponses, CustomFieldType, ExternalMemberInviteData, InviteType } from '../../types/invites';
import { connectionService, invitationService, communityService, eventService } from '../../services';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import { checkMandatoryFields, isEmptyField } from '../../utils/validationUtils';
import useProfileFields from '../../hooks/useProfileFields';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import useDeleteParams from '../../hooks/useDeleteParams';
import { SuccessPageState } from '../../types/navigation';
import { createEmptyCustomFieldResponses, formatCustomFieldResponsesFromCustomFields } from '../../utils/formatUtils';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import ContactConfirmationModal from '../../components/modals/LocalContactConfirmationModal';
import useAuth from '../../hooks/useAuth';
import AddFieldsHeader from '../../components/headers/AddFieldsHeader';
import { filterPresentFields } from '../../utils/filterUtils';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';
import NoRequestedDataCommunityInvitePage from '../common/NoRequestedDataCommunityInvitePage';
import { CommunityOverview } from '../../components/misc/CommunityOverview';
import AttendanceComponent from '../../components/misc/AttendanceComponent';
import { EventParticipationAnswer, PersonalEvent } from '../../types/event';
import { checkInviteEmailMismatch } from '../../utils/inviteUtils';

export default function AuthenticatedLocalContactInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  const auth = useAuth();
  useDeleteParams('link');

  const token = params.get('invitationId') || params.get('token');
  const eventId = params.get('eventId');

  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [invite, setInvite] = useState<ExternalMemberInviteData>();
  const [validate, setValidate] = useState<boolean>(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [noDataSharedModalOpen, setNoDataSharedModalOpen] = useState(false);
  const [event, setEvent] = useState<PersonalEvent>();
  const [eventAnswer, setEventAnswer] = useState<EventParticipationAnswer | null>(null);
  const [eventAnswered, setEventAnswered] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }

    invitationService
      .getExternalMemberInvite(token)
      .then((inv) => {
        setInvite(inv);
        const preSelectedValues = filterPresentFields(
          profileFields,
          inv.receivedData?.filter((f) => !isEmptyField(f)) ?? [],
        );

        setSelectedData(preSelectedValues);

        setCustomFieldResponses(
          formatCustomFieldResponsesFromCustomFields(inv?.customFields, inv?.customFieldResponses ?? []),
        );
        if (auth?.alias !== inv?.alias) setConfirmationModalOpen(true);
      })

      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });

    if (eventId) {
      eventService
        .getEventWithToken(token, 'external-invite')
        .then((evt) => {
          setEvent(evt);
        })
        .catch(() => {
          toast.error(t('toast.error.general.eventNotFound'));
        });
    }
  }, []);

  const fetchedSharedData = useRef(false);
  useEffect(() => {
    if (!invite || profileFields.length === 0 || fetchedSharedData.current) return;
    fetchedSharedData.current = true;

    if (invite.businessId) {
      connectionService
        .getSharedDataWithBusiness(invite.businessId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    } else if (invite.communityId) {
      connectionService
        .getSharedDataWithCommunity(invite.communityId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    }
  }, [invite, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setNoDataSharedModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    setValidate(true);
    if (!checkMandatoryFields(invite!, selectedData, customFieldResponses)) return;

    if (eventId && !eventAnswered) {
      toast.error(t('toast.error.general.pleaseAnswerEvent'));
      return;
    }

    await connectionService.acceptExternalMemberInvite(invite!.dashboardId, token!, {
      sharedPropertiesWithMembers: [],
      customFieldResponses,
      sharedPropertiesWithDashboard: selectedData.filter((x) => x.id !== undefined).map((x) => +x.id!),
    });

    if (eventId && eventAnswer) {
      await eventService.respondToEvent(eventId, eventAnswer);
    }

    try {
      await communityService.fetchCommunities();
    } catch (error) {
      // Ignore fetch error - list will update on next load
    }

    const state: SuccessPageState = {
      translationKey: `page.common.success.message.${
        invite?.businessId !== undefined ? 'businessCommunityInvite' : 'businessInvite'
      }`,
      translationOptions: {
        businessName: invite?.name?.replace('+', ' ') || '',
      },
      networkInvite: invite,
    };

    if (eventId && invite?.communityId) {
      navigate('/community/events', { state: { communityId: invite.communityId } });
    } else if (invite?.communityId && invite?.communityFeatures?.EASY_SHARING) {
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state }, replace: true });
    } else {
      navigate('/success', { state });
      toast.success(t('toast.success.connectionCreated'));
    }
  };

  const handleEventAnswerChange = (answer: EventParticipationAnswer) => {
    setEventAnswer(answer);
    setEventAnswered(true);
  };

  const handleInviteRequest = async (): Promise<void> => {
    await invitationService.requestExternalMemberInviteWithDifferentEmail(+invite!.dashboardId, token!);
    toast.success(t('toast.success.requestSent'));
    navigate('/');
  };

  const addFields = (fields: ProfileField[]): void => {
    setSelectedData([...selectedData.filter((f) => !fields.some((x) => x.dataType === f.dataType)), ...fields]);
  };

  if (invite && !invite.dashboardFeatures.MEMBER_ADMINISTRATION)
    return (
      <NoRequestedDataCommunityInvitePage
        invite={invite!}
        event={event}
        eventAnswer={eventAnswer}
        onEventAnswerChange={handleEventAnswerChange}
        onSubmit={handleSubmit}
      />
    );

  const name = invite?.name ?? ''; // of the community or the business
  const communicationName = invite?.customFields.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  const inviteEmail = invite?.receivedData?.find((x) => x.dataType === ProfileDataType.EMAIL)?.email;
  const emailCheck = checkInviteEmailMismatch(inviteEmail, auth?.email, InviteType.LOCAL_CONTACT);
  if (invite?.accountExists && emailCheck.status === 'email-mismatch')
    return (
      <ResponsePageWrapper>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <div className="flex flex-col">
          <h2 className="text-lg font-medium">{t('page.localContactInvite.authWrongAccount.title')}</h2>
          <p className="text-sm">
            {t('page.localContactInvite.authWrongAccount.message', {
              inviteeEmail: inviteEmail,
              requesterAlias: name,
            })}
          </p>
        </div>

        <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button className="underline" onClick={() => navigate('/')}>
            {t('general.cancel')}
          </Button>
          <Button variant="primary" className="px-[32px]" onClick={handleInviteRequest}>
            {t('page.localContactInvite.authWrongAccount.button')}
          </Button>
        </div>
      </ResponsePageWrapper>
    );

  return (
    <>
      <ResponsePageWrapper>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <ResponsePageWrapper.Header>
          <AddFieldsHeader requesterName={name} fields={invite?.receivedData} addFields={addFields} />
        </ResponsePageWrapper.Header>
        <div className="flex h-full flex-1 flex-col gap-4">
          {eventId && event && (
            <div className="bg-secondary-10 mt-4 rounded-lg border border-secondary-50 p-4">
              <h2 className="mb-2 text-lg font-medium">{t('page.events.infoScreen.attendance.title')}</h2>
              <p className="mb-2 text-lg">{event.title}</p>
              <p className="text-md mb-4">{t('page.events.infoScreen.attendance.question')}</p>
              <AttendanceComponent selectedAnswer={eventAnswer} setSelectedAnswer={handleEventAnswerChange} />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">{t('page.businessInvite.message', { name })}</p>
          </div>

          {communicationName && (
            <div className="border-panel-broder border-b pb-4">
              <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                {t('form.communicationName.label')} {communicationName?.mandatory && '*'}
              </h2>
              <CommunicationNameInput
                name={customFieldResponses[communicationName.customFieldId]}
                validate={validate}
                setName={(cname) =>
                  setCustomFieldResponses({
                    ...customFieldResponses,
                    [communicationName.customFieldId]: cname,
                  })
                }
                mandatory={communicationName?.mandatory}
              />
            </div>
          )}
          <UserDataForm
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            mandatoryFields={invite?.mandatoryFields || []}
            nonRequestedFields={invite?.nonRequestedFields || []}
            onePerDataType
            onlyBirthDate
          />

          <div>
            {invite?.customFields && invite?.customFields.length > 0 && (
              <>
                <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                  {t('page.shared.requestedFields')}
                </h2>
                <CustomFieldsForm
                  fields={invite?.customFields || []}
                  response={customFieldResponses}
                  setResponse={setCustomFieldResponses}
                />
              </>
            )}
          </div>
          <div className="flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <ContactConfirmationModal
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        info={{
          alias: invite?.alias || '',
          receivedData: invite?.receivedData || [],
        }}
        inviterName={name}
      />
      <NoDataSharedModal
        open={noDataSharedModalOpen}
        setOpen={setNoDataSharedModalOpen}
        requesterName={name}
        handleComplete={handleSubmit}
      />
    </>
  );
}
